import { getStackType } from "@app/deploy";
import {
  defaultDeployApp,
  defaultDeployDatabase,
  defaultDeployEndpoint,
  defaultDeployEnvironment,
  defaultDeployLlmKey,
  defaultDeployService,
  defaultDeployStack,
} from "@app/schema";
import { parseDate, parseZonedDateTime } from "@internationalized/date";
import { DateTime } from "luxon";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useMemo, useState } from "react";
import {
  AiTermsConsent,
  AnnouncementBanner,
  AppCell,
  AptibleLogo,
  AutoscalingOnStatus,
  Banner,
  Box,
  Breadcrumbs,
  Button,
  ButtonIcon,
  ButtonLink,
  COST_ESTIMATE_TOOLTIP_TEXT,
  CRUMB_VARIANTS,
  CheckBox,
  Code,
  CopyTextButton,
  CostEstimateInfoIcon,
  Count,
  CurrencyCell,
  DataTable,
  DataTableCell,
  type DataTableCellRendererProps,
  type DataTableColDef,
  DateTimeCell,
  DeploymentLabel,
  EnabledStatus,
  EndpointUrl,
  EnvironmentCell,
  FileDropTextArea,
  FormGroup,
  Group,
  IconClock,
  IconModule,
  IconPlusCircle,
  IconScaleCheck,
  IconX,
  Input,
  InputDate,
  InputDateRange,
  InputDateTimeRange,
  InputSearch,
  KeyValueGroup,
  LogViewerText,
  MultiSelect,
  OnOffLabel,
  Pill,
  PillCell,
  PreCode,
  ProgressBar,
  ProgressBarDetail,
  Radio,
  RadioGroup,
  ReorderableMultiSelect,
  ResourceDetailBar,
  ResourceDetailItem,
  ResourceDetailsTable,
  RightSizedStatus,
  ScaleDownStatus,
  ScaleRecsView,
  ScaleUpStatus,
  SearchableSelect,
  SegmentedBar,
  Select,
  type SelectOption,
  ServicesCell,
  Switch,
  TBody,
  THead,
  Table,
  Tabs,
  Td,
  TextArea,
  Th,
  Tooltip,
  Tr,
  getStackImg,
  listToTextColor,
  tokens,
} from "../shared";

const StylesWrapper = ({
  children,
  navigation,
}: { children: React.ReactNode; navigation: React.ReactNode }) => (
  <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white -mt-4 -ml-4 -mr-4">
    <div className="flex">
      <div className="w-[200px] py-6 px-6 fixed border-r border-black-100 h-full bg-off-white">
        {navigation}
      </div>
      <div className="ml-[200px] py-6 px-8 overflow-y-auto w-full mr-2 flex flex-col gap-6">
        {children}
      </div>
    </div>
  </div>
);

const StylesNavigation = () => (
  <nav className="flex flex-col gap-2">
    <div className="mb-4">
      <AptibleLogo />
    </div>
    <p>
      <b>STYLES</b>
    </p>
    {[
      { name: "Colors", to: "#colors" },
      { name: "Typography", to: "#typography" },
      { name: "AI Terms", to: "#ai-terms" },
      { name: "Buttons", to: "#buttons" },
      { name: "Banners", to: "#banners" },
      { name: "Tables", to: "#tables" },
      { name: "DataTable", to: "#datatables" },
      { name: "Forms", to: "#forms" },
      { name: "Progress Bars", to: "#progress-bars" },
      { name: "Segmented Bars", to: "#segmented-bars" },
      { name: "Logs", to: "#logs" },
      { name: "Pills", to: "#pills" },
      { name: "Labels", to: "#labels" },
      { name: "Counts", to: "#counts" },
      { name: "Navigation", to: "#navigation" },
      { name: "Icons", to: "#icons" },
      { name: "Tooltips", to: "#tooltips" },
      { name: "Resource Detail Boxes", to: "#resource-detail-boxes" },
      { name: "Boxes", to: "#boxes" },
      { name: "QR Example", to: "#qrexample" },
    ].map(({ name, to }) => (
      <a className={tokens.type["table link"]} href={to} key={to}>
        <div className="flex items-center">
          <div>{name}</div>
        </div>
      </a>
    ))}
  </nav>
);

const Banners = () => (
  <div className="pt-8 space-y-4">
    <h1 id="banners" className={tokens.type.h1}>
      Banners
    </h1>
    <Banner className="mt-2" variant="success">
      Success banner with{" "}
      <a href="/" className="underline">
        a link
      </a>
    </Banner>
    <Banner className="mt-2" variant="progress">
      Progress banner with{" "}
      <a href="/" className="underline">
        a link
      </a>
    </Banner>
    <Banner className="mt-2" variant="info">
      Info banner with{" "}
      <a href="/" className="underline">
        a link
      </a>
    </Banner>
    <Banner className="mt-2" variant="warning">
      Warning banner with{" "}
      <a href="/" className="underline">
        a link
      </a>
    </Banner>
    <Banner className="mt-2" variant="error">
      Error banner with{" "}
      <a href="/" className="underline">
        a link
      </a>
    </Banner>
    <Banner className="mt-2" variant="success">
      This is a success banner with text wrapping enabled. It demonstrates how
      longer content flows naturally within the banner container while
      maintaining proper styling and alignment.
    </Banner>
    <AnnouncementBanner
      dismissKey="styles-preview"
      badge="SEP 2026"
      icon={<span>🚀</span>}
      title="New: Platform Improvements"
      description="Container efficiency insights, AI Gateway usage breakdowns, and improved compliance reporting are now available."
      link={{
        href: "https://www.aptible.com/changelog",
        label: "View changelog",
      }}
    />
  </div>
);

const Tables = () => (
  <div className="pt-8 space-y-4">
    <h1 id="tables" className={tokens.type.h1}>
      Tables
    </h1>

    <Table>
      <THead>
        <Th>Header 1</Th>
        <Th>Header 2</Th>
        <Th>Header 3</Th>
        <Th>Header 4</Th>
        <Th>Header 5</Th>
        <Th>Header 6</Th>
        <Th>Header 7</Th>
        <Th>Header 8</Th>
        <Th>Header 9</Th>
        <Th>Header 10</Th>
      </THead>

      <TBody>
        <Tr>
          <Td>
            <RightSizedStatus />
          </Td>
          <Td>Row 1 Value 2</Td>
          <Td>Row 1 Value 3</Td>
          <Td>Row 1 Value 4</Td>
          <Td>Row 1 Value 5</Td>
          <Td>Row 1 Value 6</Td>
          <Td>Row 1 Value 7</Td>
          <Td>Row 1 Value 8</Td>
          <Td>Row 1 Value 9</Td>
          <Td>Row 1 Value 10</Td>
        </Tr>
        <Tr>
          <Td>
            <RightSizedStatus />
          </Td>
          <Td>Row 2 Value 2</Td>
          <Td>Row 2 Value 3</Td>
          <Td>Row 2 Value 4</Td>
          <Td>Row 2 Value 5</Td>
          <Td>Row 2 Value 6</Td>
          <Td>Row 2 Value 7</Td>
          <Td>Row 2 Value 8</Td>
          <Td>Row 2 Value 9</Td>
          <Td>Row 2 Value 10</Td>
        </Tr>
        <Tr>
          <Td>
            <ScaleUpStatus />
            <span>View Metrics</span>
          </Td>
          <Td>Row 3 Value 2</Td>
          <Td>Row 3 Value 3</Td>
          <Td>Row 3 Value 4</Td>
          <Td>Row 3 Value 5</Td>
          <Td>Row 3 Value 6</Td>
          <Td>Row 3 Value 7</Td>
          <Td>Row 3 Value 8</Td>
          <Td>Row 3 Value 9</Td>
          <Td>Row 3 Value 10</Td>
        </Tr>
        <Tr>
          <Td>
            <ScaleDownStatus />
            <span>Save up to $10.42</span>
          </Td>
          <Td>Row 4 Value 2</Td>
          <Td>Row 4 Value 3</Td>
          <Td>Row 4 Value 4</Td>
          <Td>Row 4 Value 5</Td>
          <Td>Row 4 Value 6</Td>
          <Td>Row 4 Value 7</Td>
          <Td>Row 4 Value 8</Td>
          <Td>Row 4 Value 9</Td>
          <Td>Row 4 Value 10</Td>
        </Tr>
        <Tr>
          <Td>Row 5 Value 1</Td>
          <Td>Row 5 Value 2</Td>
          <Td>Row 5 Value 3</Td>
          <Td>Row 5 Value 4</Td>
          <Td>Row 5 Value 5</Td>
          <Td>Row 5 Value 6</Td>
          <Td>Row 5 Value 7</Td>
          <Td>Row 5 Value 8</Td>
          <Td>Row 5 Value 9</Td>
          <Td>Row 5 Value 10</Td>
        </Tr>
      </TBody>
    </Table>
  </div>
);

const DataTables = () => {
  type DemoRow = {
    appId: string;
    envId: string;
    name: string;
    cost: number;
    status: string;
    date: string;
    services: ReturnType<typeof defaultDeployService>[];
  };

  const rowData: DemoRow[] = [
    {
      appId: "63798",
      envId: "8458",
      name: "my-app",
      cost: 1423.5,
      status: "active",
      date: "2024-03-15T14:30:00Z",
      services: [
        defaultDeployService({
          containerMemoryLimitMb: 512,
          containerCount: 2,
        }),
        defaultDeployService({
          containerMemoryLimitMb: 1024,
          containerCount: 1,
        }),
      ],
    },
    {
      appId: "63797",
      envId: "8456",
      name: "api-service",
      cost: 892.25,
      status: "pending",
      date: "2024-02-28T09:15:00Z",
      services: [
        defaultDeployService({
          containerMemoryLimitMb: 2048,
          containerCount: 3,
        }),
      ],
    },
    {
      appId: "63796",
      envId: "8457",
      name: "worker-process",
      cost: 234.75,
      status: "inactive",
      date: "2024-01-10T20:45:00Z",
      services: [
        defaultDeployService({
          containerMemoryLimitMb: 512,
          containerCount: 1,
        }),
      ],
    },
  ];

  const statusVariantMap = {
    active: "success" as const,
    pending: "pending" as const,
    inactive: "error" as const,
  };

  const colDefs: DataTableColDef<DemoRow>[] = [
    {
      field: "name",
      headerName: "DataTableCell",
      cellRenderer: ({ data }: DataTableCellRendererProps<DemoRow>) => (
        <DataTableCell label={data.name} description="label + description" />
      ),
    },
    {
      field: "appId",
      headerName: "AppCell",
      headerTooltip:
        "Requires Redux store data — shows empty state in this demo",
      cellRenderer: ({ data }: DataTableCellRendererProps<DemoRow>) => (
        <AppCell id={data.appId} />
      ),
    },
    {
      field: "envId",
      headerName: "EnvironmentCell",
      headerTooltip:
        "Requires Redux store data — shows empty state in this demo",
      cellRenderer: ({ data }: DataTableCellRendererProps<DemoRow>) => (
        <EnvironmentCell id={data.envId} />
      ),
    },
    {
      field: "services",
      headerName: "ServicesCell",
      cellRenderer: ({ data }: DataTableCellRendererProps<DemoRow>) => (
        <ServicesCell services={data.services} />
      ),
    },
    {
      field: "cost",
      headerName: "CurrencyCell",
      cellRenderer: ({ data }: DataTableCellRendererProps<DemoRow>) => (
        <CurrencyCell value={data.cost} />
      ),
    },
    {
      field: "status",
      headerName: "PillCell",
      cellRenderer: ({ data }: DataTableCellRendererProps<DemoRow>) => (
        <PillCell value={data.status} valueToVariantMap={statusVariantMap} />
      ),
    },
    {
      field: "date",
      headerName: "DateTimeCell",
      cellRenderer: ({ data }: DataTableCellRendererProps<DemoRow>) => (
        <DateTimeCell date={data.date} />
      ),
    },
  ];

  const [selectedRows, setSelectedRows] = useState<DemoRow[]>([]);

  return (
    <div className="pt-8 space-y-4">
      <h1 id="datatables" className={tokens.type.h1}>
        DataTable
      </h1>
      <DataTable rowData={rowData} colDefs={colDefs} />

      <h2 className={tokens.type.h2}>Row Selection</h2>
      <p>
        Pass <Code>rowSelection</Code> to add a checkbox column, and omit it to
        take the column away. Selecting rows calls{" "}
        <Code>onSelectionChanged</Code> with every selected row, and{" "}
        <Code>isRowSelected</Code> seeds the initial selection (
        <Code>worker-process</Code> here). Pair it with <Code>rowId</Code> —
        without stable row ids, ag-grid drops the selection whenever the caller
        re-renders with a new <Code>rowData</Code> array.
      </p>
      <p>
        With selection on, the search box also takes <Code>selected:true</Code>{" "}
        and <Code>selected:false</Code>. Both read as a snapshot from when the
        term was typed, since ag-grid doesn't re-filter as rows are checked.
      </p>
      <DataTable
        rowData={rowData}
        colDefs={colDefs.slice(0, 3)}
        rowSelection="multiRow"
        rowId={(row) => row.appId}
        isRowSelected={(row) => row.name === "worker-process"}
        onSelectionChanged={setSelectedRows}
      />
      <p>
        Selected:{" "}
        {selectedRows.length === 0
          ? "none"
          : selectedRows.map((row) => row.name).join(", ")}
      </p>
    </div>
  );
};

const Data = () => {
  return (
    <div className="pt-8 space-y-4">
      <h1 id="data" className={tokens.type.h1}>
        Data
      </h1>

      <div className="w-[300px]">
        <KeyValueGroup
          data={[
            { key: "Key1", value: "Value1" },
            { key: "Key2", value: "Value2" },
            { key: "Key3", value: "Value3" },
          ]}
        />
      </div>
    </div>
  );
};

const Forms = () => {
  const [search, setSearch] = useState("");
  const onChange = (ev: React.ChangeEvent<HTMLInputElement>) =>
    setSearch(ev.currentTarget.value);

  const [choice, setChoice] = useState<string>("");
  const [formInput, setFormInput] = useState("");
  const [fileDropText, setFileDropText] = useState(
    "Drop a text file into this textarea",
  );
  const [dateInput, setDateInput] = useState(DateTime.utc().toISODate());
  const [dateTimeInput, setDateTimeInput] = useState(
    DateTime.utc().toISO({ extendedZone: true }),
  );
  const selectOption = (option: SelectOption) => {
    setChoice(option.value);
  };
  const [multiValues, setMultiValues] = useState<string[]>([]);

  const options: SelectOption[] = Array(8)
    .fill(0)
    .map((_, idx) => ({
      label: `Select Menu Option ${idx + 1}`,
      value: `option_${idx + 1}`,
    }));

  const largeOptions: SelectOption[] = useMemo(
    () =>
      Array.from({ length: 5000 }, (_, i) => ({
        label: `Account ${i + 1}`,
        value: `account_${i + 1}`,
      })),
    [],
  );
  const [largeMultiValues, setLargeMultiValues] = useState<string[]>([]);

  const largeOptions2: SelectOption[] = useMemo(
    () =>
      Array.from({ length: largeOptions.length }, (_, i) => ({
        label: `Account ${i + 1}`,
        value: `account_${i + 1}`,
      })),
    [],
  );
  const [largeMultiValues2, setLargeMultiValues2] = useState<string[]>([]);

  const [searchableValue, setSearchableValue] = useState<string>("");
  const [largeSearchableValue, setLargeSearchableValue] = useState<string>("");
  const [largeSearchableValue2, setLargeSearchableValue2] =
    useState<string>("");

  const [reorderOptions, setReorderOptions] = useState<SelectOption[]>([
    { label: "Operation", value: "operation" },
    { label: "Target", value: "target" },
    { label: "Additional Info", value: "additionalInfo" },
    { label: "Status", value: "status" },
    { label: "Actor", value: "actor" },
    { label: "Environment", value: "environment" },
    { label: "Occurred At", value: "occurredAt" },
  ]);
  const [reorderSelected, setReorderSelected] = useState<string[]>(
    reorderOptions.map((o) => o.value),
  );
  const selectedOption = [].find(
    (option: SelectOption) => option.value === choice,
  );

  return (
    <div className="pt-8 flex flex-col gap-2 w-1/3">
      <h1 id="forms" className={tokens.type.h1}>
        Forms
      </h1>
      <InputSearch
        className="mt-4"
        placeholder="Search field..."
        search={search}
        onChange={onChange}
      />
      <Input
        className="mt-4"
        name="app-handle"
        type="text"
        value={formInput}
        onChange={(e) => setFormInput(e.currentTarget.value)}
        autoComplete="name"
        data-testid="input-name"
        id="input-name"
        placeholder="Input"
      />
      <Input
        className="mt-4"
        name="app-handle"
        type="text"
        value={formInput}
        onChange={(e) => setFormInput(e.currentTarget.value)}
        disabled
        autoComplete="name"
        data-testid="input-name"
        id="input-name"
        placeholder="Disabled input"
      />
      <div className="my-4">
        <h3 className={tokens.type.h3}>Date Picker</h3>
      </div>
      <InputDate
        className="mt-4 max-w-full"
        name="app-date"
        granularity="day"
        defaultValue={parseDate(dateInput)}
        value={parseDate(dateInput)}
        onChange={(d) => d && setDateInput(d.toString())}
        autoComplete="name"
        data-testid="input-name"
        id="input-name"
      />
      <InputDate
        className="mt-4 max-w-full"
        name="app-datetime"
        granularity="minute"
        defaultValue={parseZonedDateTime(dateTimeInput)}
        value={parseZonedDateTime(dateTimeInput)}
        onChange={(d) => d && setDateTimeInput(d.toString())}
        autoComplete="name"
        data-testid="input-name"
        id="input-name"
      />
      <InputDate
        className="mt-4 max-w-full"
        isDisabled
        name="app-date-disabled"
        granularity="day"
        autoComplete="name"
        data-testid="input-name"
        id="input-name"
      />
      <div className="my-4">
        <h3 className={tokens.type.h3}>Date Range Picker</h3>
      </div>
      <InputDateRange
        className="mt-4 max-w-full"
        granularity="day"
        data-testid="input-name"
        defaultValue={{
          start: parseZonedDateTime("2025-02-03T08:45:00[America/Los_Angeles]"),
          end: parseZonedDateTime("2025-02-14T08:45:00[America/Los_Angeles]"),
        }}
        id="date-rangeinput-name-1"
      />
      <p className="mt-4 mb-0 pb-0">Future selection disabled:</p>
      <InputDateRange
        className="mt-4 max-w-full"
        granularity="day"
        data-testid="input-name"
        disableFuture
        defaultValue={{
          start: parseZonedDateTime("2025-01-03T08:45:00[America/Los_Angeles]"),
          end: parseZonedDateTime("2025-01-05T08:45:00[America/Los_Angeles]"),
        }}
        id="date-range-input-name-2"
      />
      <p className="mt-4 mb-0 pb-0">
        Future selection disabled with max range selection:
      </p>
      <InputDateRange
        className="mt-4 max-w-full"
        granularity="day"
        data-testid="input-name"
        disableFuture
        maxRangeDays={5}
        defaultValue={{
          start: parseZonedDateTime("2025-01-03T08:45:00[America/Los_Angeles]"),
          end: parseZonedDateTime("2025-01-05T08:45:00[America/Los_Angeles]"),
        }}
        id="date-range-input-name-2"
      />
      <div className="my-4">
        <h3 className={tokens.type.h3}>Date-Time Range Picker</h3>
      </div>
      <InputDateTimeRange
        className="mt-4 max-w-full"
        granularity="minute"
        data-testid="input-datetime-range"
        defaultValue={{
          start: parseZonedDateTime("2025-02-03T09:00:00[America/Los_Angeles]"),
          end: parseZonedDateTime("2025-02-03T17:00:00[America/Los_Angeles]"),
        }}
        id="datetime-range-input-name-1"
      />
      <p className="mt-4 mb-0 pb-0">Future selection disabled:</p>
      <InputDateTimeRange
        className="mt-4 max-w-full"
        granularity="minute"
        data-testid="input-datetime-range-2"
        disableFuture
        defaultValue={{
          start: parseZonedDateTime("2025-01-03T08:00:00[America/Los_Angeles]"),
          end: parseZonedDateTime("2025-01-05T18:30:00[America/Los_Angeles]"),
        }}
        id="datetime-range-input-name-2"
      />
      <p className="mt-4 mb-0 pb-0">With 12-hour format:</p>
      <InputDateTimeRange
        className="mt-4 max-w-full"
        granularity="minute"
        hourCycle={12}
        data-testid="input-datetime-range-3"
        defaultValue={{
          start: parseZonedDateTime("2025-02-03T09:00:00[America/Los_Angeles]"),
          end: parseZonedDateTime("2025-02-03T17:00:00[America/Los_Angeles]"),
        }}
        id="datetime-range-input-name-3"
      />
      <h3 className={`${tokens.type.h3} mt-4`}>Select</h3>
      <Select
        className="mt-4"
        onSelect={selectOption}
        value={selectedOption}
        options={options}
      />
      <h3 className={`${tokens.type.h3} mt-4`}>SearchableSelect</h3>
      <SearchableSelect
        className="mt-2"
        options={options}
        value={searchableValue}
        onChange={(val) => setSearchableValue(val || "")}
      />
      <h3 className={`${tokens.type.h3} mt-4`}>
        SearchableSelect ({largeOptions.length} items, virtualized)
      </h3>
      <SearchableSelect
        className="mt-2"
        options={largeOptions}
        value={largeSearchableValue}
        onChange={(val) => setLargeSearchableValue(val || "")}
      />
      <h3 className={`${tokens.type.h3} mt-4`}>
        SearchableSelect ({largeOptions2.length} items, <em>not</em>{" "}
        virtualized)
      </h3>
      <SearchableSelect
        className="mt-2"
        options={largeOptions2}
        value={largeSearchableValue2}
        onChange={(val) => setLargeSearchableValue2(val || "")}
        disableVirtualization={true}
      />
      <h3 className={`${tokens.type.h3} mt-4`}>MultiSelect</h3>
      <MultiSelect
        className="mt-2"
        options={options}
        value={multiValues}
        onChange={(vals: string[]) => setMultiValues(vals)}
      />
      <h3 className={`${tokens.type.h3} mt-4`}>
        MultiSelect ({largeOptions.length} items, virtualized)
      </h3>
      <MultiSelect
        className="mt-2"
        options={largeOptions}
        value={largeMultiValues}
        onChange={(vals: string[]) => setLargeMultiValues(vals)}
      />
      <h3 className={`${tokens.type.h3} mt-4`}>
        MultiSelect ({largeOptions2.length} items, <em>not</em> virtualized)
      </h3>
      <MultiSelect
        className="mt-2"
        options={largeOptions2}
        value={largeMultiValues2}
        onChange={(vals: string[]) => setLargeMultiValues2(vals)}
        disableVirtualization={true}
      />
      <h3 className={`${tokens.type.h3} mt-4`}>ReorderableMultiSelect</h3>
      <ReorderableMultiSelect
        className="mt-2"
        options={reorderOptions}
        selectedValues={reorderSelected}
        onSelectionChange={setReorderSelected}
        onOrderChange={(ordered) => {
          const optionMap = new Map(reorderOptions.map((o) => [o.value, o]));
          setReorderOptions(
            ordered.map((v) => optionMap.get(v) as SelectOption),
          );
        }}
      />
      <div className="mt-4">
        <RadioGroup name="service" selected="cmd" onSelect={() => {}}>
          <Radio value="unchecked">Radio unchecked</Radio>
          <Radio value="cmd">Radio checked</Radio>
          <Radio value="disabled" disabled={true}>
            Radio disabled
          </Radio>
        </RadioGroup>
      </div>

      <div className="mt-4">
        <CheckBox checked label="Check Box" />
        <CheckBox checked={false} label="Check Box" />
        <CheckBox checked label="Check Box - size sm" variantSize="sm" />
        <CheckBox
          checked={false}
          label="Check Box - size sm"
          variantSize="sm"
        />
        <CheckBox checked label="Check Box - simple" variant="simple" />
        <CheckBox checked={false} label="Check Box - simple" variant="simple" />
        <CheckBox
          checked
          label="Check Box - simple - size sm"
          variant="simple"
          variantSize="sm"
        />
        <CheckBox
          checked={false}
          label="Check Box - simple - size sm"
          variant="simple"
          variantSize="sm"
        />
      </div>

      <div className="mt-4">
        <h3 className={tokens.type.h3}>Switches</h3>
        <div className="mt-4 space-y-4">
          <Switch checked={true}>Enabled Switch</Switch>
          <Switch checked={false}>Disabled Switch</Switch>
          <Switch checked={true} disabled>
            Disabled & Checked
          </Switch>
        </div>
      </div>

      <TextArea
        className={`${tokens.type.textarea} mt-4`}
        defaultValue="Editable textarea"
      />
      <FileDropTextArea
        aria-label="File drop textarea"
        className="mt-4"
        value={fileDropText}
        onValueChange={setFileDropText}
      />

      <div className="my-4">
        <h3 className={tokens.type.h3}>Form Groups</h3>
      </div>

      <FormGroup htmlFor="input - name" label="Label">
        <Input
          name="app-handle"
          type="text"
          value={formInput}
          onChange={(e) => setFormInput(e.currentTarget.value)}
          autoComplete="name"
          data-testid="input-name"
          id="input-name"
          placeholder="Input"
        />
      </FormGroup>
    </div>
  );
};

const Colors = () => (
  <div className="space-y-4">
    <h1 id="colors" className={tokens.type.h1}>
      Colors
    </h1>

    <h3 className={tokens.type.h3}>Main Colors</h3>
    <div className="flex my-2">
      <div className="rounded-full bg-black w-[32px] h-[32px] block mr-2" />
      <p className="leading-8">Black</p>
    </div>
    <div className="flex my-2">
      <div className="rounded-full bg-white border-solid border border-black w-[32px] h-[32px] block mr-2" />
      <p className="leading-8">White</p>
    </div>
    <div className="flex my-2">
      <div className="rounded-full bg-indigo w-[32px] h-[32px] block mr-2" />
      <p className="leading-8">Indigo</p>
    </div>
    <div className="flex my-2">
      <div className="rounded-full bg-yellow w-[32px] h-[32px] block mr-2" />
      <p className="leading-8">Yellow</p>
    </div>
    <div className="flex my-2">
      <div className="rounded-full bg-gold w-[32px] h-[32px] block mr-2" />
      <p className="leading-8">Gold</p>
    </div>
    <div className="flex my-2">
      <div className="rounded-full bg-forest w-[32px] h-[32px] block mr-2" />
      <p className="leading-8">Forest</p>
    </div>
    <div className="flex my-2">
      <div className="rounded-full bg-red w-[32px] h-[32px] block mr-2" />
      <p className="leading-8">Red</p>
    </div>
    <h3 className={tokens.type.h3}>Misc. Colors</h3>
    <div className="flex my-2">
      <div className="rounded-full bg-off-white border-solid border border-black w-[32px] h-[32px] block mr-2" />
      <p className="leading-8">Off-White</p>
    </div>
    <div className="flex my-2">
      <div className="rounded-full bg-lime w-[32px] h-[32px] block mr-2" />
      <p className="leading-8">Lime</p>
    </div>
    <div className="flex my-2">
      <div className="rounded-full bg-cyan w-[32px] h-[32px] block mr-2" />
      <p className="leading-8">Cyan</p>
    </div>
    <div className="flex my-2">
      <div className="rounded-full bg-brown w-[32px] h-[32px] block mr-2" />
      <p className="leading-8">Brown</p>
    </div>
    <div className="flex my-2">
      <div className="rounded-full bg-lavender w-[32px] h-[32px] block mr-2" />
      <p className="leading-8">Lavender</p>
    </div>
    <div className="flex my-2">
      <div className="rounded-full bg-plum w-[32px] h-[32px] block mr-2" />
      <p className="leading-8">Plum</p>
    </div>
  </div>
);

const Typography = () => (
  <div className="pt-8 space-y-4">
    <h1 id="typography" className={tokens.type.h1}>
      Typography
    </h1>

    <h1 className={tokens.type.h1}>H1 Heading</h1>
    <h2 className={tokens.type.h2}>H2 Heading</h2>
    <h3 className={tokens.type.h3}>H3 Heading</h3>
    <h4 className={tokens.type.h4}>H4 Heading</h4>
    <p>Paragraph text</p>

    <p>
      <a href="/">Link Text (Unstyled)</a>
    </p>
    <p>
      <a className={tokens.type.link} href="/">
        Link Text (Styled as tokens.type.link)
      </a>
    </p>

    <p>Paragraph variants</p>
    <p className={tokens.type["small semibold darker"]}>
      Small semibold darker
    </p>
    <p className={tokens.type["small normal darker"]}>Small normal darker</p>
    <p className={tokens.type["small normal lighter"]}>Small normal lighter</p>
    <p className={tokens.type["medium label"]}>Medium label</p>

    <p className={tokens.type.textarea}>Textarea format</p>

    <PreCode
      segments={listToTextColor(["git", "push", "origin", "main"])}
      allowCopy
    />
  </div>
);

const AiTerms = () => (
  <div className="pt-8 space-y-4">
    <h1 id="ai-terms" className={tokens.type.h1}>
      AI Terms
    </h1>

    <AiTermsConsent action="enrolling" />
  </div>
);

const Buttons = () => (
  <div className="pt-8 space-y-4">
    <h1 id="buttons" className={tokens.type.h1}>
      Buttons
    </h1>

    <h3 className={tokens.type.h3}>Button Variants</h3>
    <Button className="mt-2">Button Default</Button>
    <Button className="mt-2" variant="primary">
      Primary Button
    </Button>
    <Button className="mt-2" variant="delete">
      Delete Button
    </Button>
    <Button className="mt-2" variant="secondary">
      Secondary Button
    </Button>
    <Button className="mt-2" variant="success">
      Success Button
    </Button>
    <Button className="mt-2" variant="white">
      White Button
    </Button>

    <ButtonIcon icon={<IconPlusCircle />}>Button Icon</ButtonIcon>

    <ButtonLink className="w-fit" to="#">
      Button Link
    </ButtonLink>

    <h3 className={tokens.type.h3}>Button Sizes</h3>
    <Button className="mt-2" size="xs">
      Extra Small
    </Button>
    <Button className="mt-2" size="sm">
      Small
    </Button>
    <Button className="mt-2" size="md">
      Medium
    </Button>
    <Button className="mt-2" size="lg">
      Large
    </Button>
    <Button className="mt-2" size="xl">
      Extra Large
    </Button>

    <h3 className={tokens.type.h3}>Button States</h3>
    <Button className="mt-2" disabled>
      Button Disabled
    </Button>
    <Button className="mt-2" isLoading />
  </div>
);

const ProgressBars = () => (
  <div className="pt-8 space-y-4">
    <h1 id="progress-bars" className={tokens.type.h1}>
      Progress Bars
    </h1>

    <h3 className={tokens.type.h3}>Basic Progress Bar</h3>
    <div className="w-[400px] space-y-4">
      <ProgressBar value={25} maxValue={100} />
      <ProgressBar value={50} maxValue={100} />
      <ProgressBar value={75} maxValue={100} />
      <ProgressBar value={90} maxValue={100} />
    </div>

    <h3 className={tokens.type.h3}>Progress Bar Variants</h3>
    <div className="w-[400px] space-y-4">
      <div>
        <p className="text-sm text-gray-600 mb-1">Default</p>
        <ProgressBar value={60} maxValue={100} variant="default" />
      </div>
      <div>
        <p className="text-sm text-gray-600 mb-1">Info</p>
        <ProgressBar value={60} maxValue={100} variant="info" />
      </div>
      <div>
        <p className="text-sm text-gray-600 mb-1">Success</p>
        <ProgressBar value={60} maxValue={100} variant="success" />
      </div>
      <div>
        <p className="text-sm text-gray-600 mb-1">Warning</p>
        <ProgressBar value={60} maxValue={100} variant="warning" />
      </div>
      <div>
        <p className="text-sm text-gray-600 mb-1">Error</p>
        <ProgressBar value={60} maxValue={100} variant="error" />
      </div>
    </div>

    <h3 className={tokens.type.h3}>Progress Bar Sizes</h3>
    <div className="w-[400px] space-y-4">
      <ProgressBar value={60} maxValue={100} height="sm" />
      <ProgressBar value={60} maxValue={100} height="md" />
      <ProgressBar value={60} maxValue={100} height="lg" />
    </div>

    <h3 className={tokens.type.h3}>Progress Bar Detail Component</h3>
    <div className="w-[400px]">
      <ProgressBarDetail
        label="Monthly Spend Limit"
        value={4302.17}
        maxValue={5000}
        unit="USD"
        resetDate="2026-02-01T00:00:00Z"
      />
    </div>

    <div className="w-[400px] mt-4">
      <ProgressBarDetail
        label="API Requests"
        value={750000}
        maxValue={1000000}
        unit="requests"
      />
    </div>
  </div>
);

const SegmentedBars = () => (
  <div className="pt-8 space-y-4">
    <h1 id="segmented-bars" className={tokens.type.h1}>
      Segmented Bars
    </h1>

    <h3 className={tokens.type.h3}>Container Efficiency</h3>
    <div className="w-[400px]">
      <SegmentedBar
        segments={[
          { label: "Under-provisioned", pct: 15, fill: "bg-red-500" },
          { label: "Over-provisioned", pct: 25, fill: "bg-indigo-200" },
          { label: "Rightsized", pct: 60, fill: "bg-gray-200" },
        ]}
      />
    </div>

    <h3 className={tokens.type.h3}>Single Segment</h3>
    <div className="w-[400px]">
      <SegmentedBar
        segments={[{ label: "Rightsized", pct: 100, fill: "bg-gray-200" }]}
      />
    </div>
  </div>
);

const Logs = () => {
  const log = `2023-06-14 16:45:39 +0000 INFO -- : Starting Database backup operation with ID: 3946
2023-06-14 16:45:39 +0000 INFO -- : Obtaining dependency information for asgiref==3.7.2 from https://files.pythonhosted.org/packages/9b/80/b9051a4a07ad231558fcd8ffc89232711b4e618c15cb7a392a17384bbeef/asgiref-3.7.2-py3-none-any.whl.metadata
2023-06-14 16:45:39 +0000 INFO -- : STARTING: Snapshot EBS volume
2023-06-14 16:45:40 +0000 WARN -- : WAITING FOR: Snapshot EBS volume
2023-06-14 16:45:52 +0000 INFO -- : WAITING FOR: Snapshot EBS volume
2023-06-14 16:46:04 +0000 INFO -- : WAITING FOR: Snapshot EBS volume
2023-06-14 16:46:16 +0000 ERROR -- : WAITING FOR: Snapshot EBS volume
2023-06-14 16:46:29 +0000 INFO -- : WAITING FOR: Snapshot EBS volume
2023-06-14 16:46:41 +0000 INFO -- : WAITING FOR: Snapshot EBS volume
2023-06-14 16:46:45 +0000 INFO -- : COMPLETED (after 65.95s): Snapshot EBS volume
2023-06-14 16:46:45 +0000 INFO -- : STARTING: Commit Backup in API
2023-06-14 16:46:46 +0000 INFO -- : COMPLETED (after 0.2s): Commit Backup in API
`;
  return (
    <div className="pt-8 space-y-4 w-[500px]">
      <h1 id="logs" className={tokens.type.h1}>
        Logs
      </h1>
      <LogViewerText text={log} />
    </div>
  );
};

const Pills = () => (
  <div className="pt-8 space-y-4">
    <h1 id="pills" className={`${tokens.type.h1} flex items-center gap-1.5`}>
      Pills
      <CostEstimateInfoIcon
        text="Pills are primarily used for status. Think: container efficiency & operations. They should be used sparingly (ideally no more than one column per table), otherwise they will compete visually and become less meaningful. Note that they can appear with or without icons."
        ariaLabel="More about pills"
      />
    </h1>
    <div className="mt-4 flex flex-col items-start gap-4">
      <Pill variant="error" icon={<IconX variant="sm" color="#AD1A1A" />}>
        Fail
      </Pill>
      <Pill
        variant="success"
        icon={<IconScaleCheck variant="sm" color="#425B21" />}
      >
        Success
      </Pill>
      <Pill variant="pending" icon={<IconClock variant="sm" />}>
        Queued
      </Pill>
      <AutoscalingOnStatus />
      <RightSizedStatus />
      <ScaleUpStatus />
      <ScaleDownStatus />
    </div>
  </div>
);

const Labels = () => (
  <div className="pt-8 space-y-4">
    <h1 id="labels" className={tokens.type.h1}>
      Labels
    </h1>
    <div className="mt-4">
      <h3 className={tokens.type.h3}>On/Off</h3>
      <div className="mt-4 flex items-center gap-4">
        <OnOffLabel on={true} />
        <OnOffLabel on={false} />
      </div>
    </div>
    <div className="mt-4">
      <h3 className={tokens.type.h3}>Enabled/Disabled</h3>
      <div className="mt-4 flex items-center gap-4">
        <EnabledStatus enabled={true} />
        <EnabledStatus enabled={false} />
      </div>
    </div>
    <div className="mt-4">
      <h3 className={tokens.type.h3}>Deployment Label</h3>
      <p className="mt-2 text-black-500">
        Tag chip used to mark deployments in a comparison view (e.g. in the
        rollback modal). May expand to include other colors if needed, but red
        should be avoided.
      </p>
      <div className="mt-4 flex items-center gap-4">
        <DeploymentLabel variant="default">Current</DeploymentLabel>
        <DeploymentLabel variant="success">Selected to restore</DeploymentLabel>
        <DeploymentLabel variant="default" onRemove={() => {}}>
          Removable
        </DeploymentLabel>
      </div>
    </div>
    <div className="mt-4">
      <h3 id="counts" className={tokens.type.h3}>
        Counts
      </h3>
      <p className="mt-2 text-black-500">
        Used to show item counts alongside tab labels
      </p>
      <div className="mt-4 flex items-center gap-4">
        <Count n={3} />
        <Count n={12} />
        <Count n={128} />
      </div>
      <p className="mt-4 text-black-500">Count is 0</p>
      <div className="mt-4 flex items-center gap-4">
        <Count n={0} />
      </div>
      <p className="mt-4 text-black-500">Loading</p>
      <div className="mt-4 flex items-center gap-4">
        <Count n={null} />
      </div>
    </div>
  </div>
);

function iconNameToFigmaName(name: string): string {
  const withoutPrefix = name.replace(/^Icon/, "");
  return `icons / ${withoutPrefix
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2")
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .toLowerCase()}`;
}

type SortColumn = "name" | "figmaName";
type SortDirection = "asc" | "desc";

const figmaFileKey = "QdkM2Z5py1EtpBh3AFE26Y";
export const iconFigmaNodes: Record<string, string | null> = {
  IconActivity: "1548-3879",
  IconAI: "2152-1615",
  IconAlertCircle: "2993-1245",
  IconAlertTriangle: "865-614",
  IconAptible: "1040-901",
  IconArrowDown: "865-453",
  IconArrowLeft: "865-454",
  IconArrowRight: "865-455",
  IconArrowUp: "865-452",
  IconAws: "1488-4285",
  IconBars3: "865-514",
  IconBeaker: null,
  IconBell: "1005-830",
  IconBitbucket: "897-820",
  IconBox: "865-493",
  IconCalendar: "865-523",
  IconCard: "1133-1169",
  IconCertificate: "1627-4776",
  IconChartSquareBar: "865-506",
  IconCheck: "865-558",
  IconCheckCircle: "865-557",
  IconCheckCircleAlt: "3051-1191",
  IconChevronDown: "865-458",
  IconChevronLeft: "865-459",
  IconChevronRight: "865-460",
  IconChevronUp: "865-461",
  IconCircleStack: "865-624",
  IconClock: null,
  IconCloud: "865-469",
  IconCommit: "932-735",
  IconCopy: "865-570",
  IconCreditCard: "1133-1169",
  IconCylinder: "865-624",
  IconCube: "865-493",
  IconDeployments: "865-470",
  IconDiagnostics: "865-506",
  IconDiff: "2053-1377",
  IconDocs: "3779-2",
  IconDocumentSearch: "865-466",
  IconDownload: "865-613",
  IconDuplicate: "865-570",
  IconEdit: "865-465",
  IconEllipsis: "865-541",
  IconEllipsisHorizontal: "865-541",
  IconEndpoint: "1783-1172",
  IconEnvelope: "905-826",
  IconExclamationCircle: "2993-1245",
  IconExternalLink: "865-467",
  IconExternalResource: "2084-1413",
  IconEyeClosed: "2070-2355",
  IconEyeOpen: "2070-2354",
  IconFileText: "2714-1975",
  IconFilter: "2674-1211",
  IconFit: "2070-2345",
  IconFolder: "1925-2181",
  IconGitBranch: "931-729",
  IconGitCommit: "932-735",
  IconGithub: "897-821",
  IconGitlab: "897-822",
  IconGlobe: "865-472",
  IconGlobeAlt: "865-472",
  IconGrid: "2748-1246",
  IconHamburger: "865-514",
  IconHeart: "1548-3879",
  IconHome: "1439-3646",
  IconInfo: "944-993",
  IconKey: "865-626",
  IconLayers: "1601-5641",
  IconHelp: "3774-3",
  IconLightbulb: "3775-2",
  IconList: "2733-1308",
  IconLock: "865-612",
  IconLogout: "865-478",
  IconMagnifyingGlass: "865-546",
  IconMap: "2038-1372",
  IconMetrics: "865-506",
  IconMinusCircle: "2993-1243",
  IconOrganization: "1727-1990",
  IconPipelines: "1893-1379",
  IconPencil: "865-465",
  IconPlusCircle: "865-581",
  IconRefresh: "1250-2839",
  IconReorder: "3037-1241",
  IconRollback: "2047-1377",
  IconScaleCheck: "865-558",
  IconScaleDown: "865-453",
  IconScaleUp: "865-452",
  IconSearch: "865-546",
  IconService: "1793-3481",
  IconSettings: "865-519",
  IconShield: "865-559",
  IconShieldCheck: "865-559",
  IconSource: "865-489",
  IconStack: "1601-5641",
  IconSwitchHorizontal: "865-456",
  IconSwitchVertical: "865-457",
  IconTarget: "2073-1375",
  IconTerminal: "2010-1371",
  IconThumbsDown: "2576-1215",
  IconThumbsUp: "1414-3614",
  IconTool: "2191-1613",
  IconTrash: "865-464",
  IconUnlock: "2653-1245",
  IconUser: "865-532",
  IconUserCircle: "865-531",
  IconUsers: "2598-1219",
  IconWorkplace: "1727-1990",
  IconX: "865-588",
  IconXCircle: "865-589",
};

export const STYLES_PAGE_EXCLUDES = new Set([
  "IconSubtleProvider",
  "IconAlertDot",
]);

const allIcons = Object.entries(IconModule)
  .filter(
    ([name, value]) =>
      name.startsWith("Icon") &&
      !STYLES_PAGE_EXCLUDES.has(name) &&
      typeof value === "function",
  )
  .map(([name, Component]) => ({
    name,
    figmaName: iconNameToFigmaName(name),
    figmaNodeId: iconFigmaNodes[name],
    Component: Component as React.FC<{ variant?: "sm" | "base" | "lg" }>,
  }));

const figmaNodeUrl = (nodeId: string) => {
  return `https://www.figma.com/design/${figmaFileKey}/Design-System?node-id=${nodeId}`;
};

const figmaLinkOrTooltip = (figmaName: string, figmaNodeId?: string | null) => {
  if (import.meta.env.DEV && figmaNodeId) {
    return (
      <a
        href={figmaNodeUrl(figmaNodeId)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 underline"
      >
        {figmaName}
      </a>
    );
  }
  if (import.meta.env.DEV) {
    return (
      <Tooltip text="Unknown Figma node ID">
        <span className="text-red-500 cursor-help">{figmaName}</span>
      </Tooltip>
    );
  }
  return <>{figmaName}</>;
};

const Icons = () => {
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState<SortColumn>("name");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const list = q
      ? allIcons.filter(
          (i) =>
            i.name.toLowerCase().includes(q) ||
            i.figmaName.toLowerCase().includes(q),
        )
      : allIcons;

    return [...list].sort((a, b) => {
      const av = a[sortCol];
      const bv = b[sortCol];
      const cmp = av.localeCompare(bv);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [search, sortCol, sortDir]);

  const toggleSort = (col: SortColumn) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const sortIndicator = (col: SortColumn) => {
    if (sortCol !== col) return "";
    return sortDir === "asc" ? " \u25B2" : " \u25BC";
  };

  return (
    <div className="pt-8 space-y-4">
      <h1 id="icons" className={tokens.type.h1}>
        Icons
      </h1>
      <div className="mt-4">
        <div className="flex items-center gap-4 mb-4">
          <h3 className={tokens.type.h3}>Icon Variants ({filtered.length})</h3>
          <input
            type="text"
            placeholder="Search icons..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1 text-sm w-64"
          />
        </div>
        <div className="flex flex-wrap gap-4 mt-4">
          <table className="border-separate border-spacing-2">
            <thead>
              <tr>
                <th
                  className="cursor-pointer select-none"
                  onClick={() => toggleSort("name")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") toggleSort("name");
                  }}
                >
                  Name{sortIndicator("name")}
                </th>
                <th
                  className="cursor-pointer select-none"
                  onClick={() => toggleSort("figmaName")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      toggleSort("figmaName");
                  }}
                >
                  Figma Name{sortIndicator("figmaName")}
                </th>
                <th>Small</th>
                <th>Base</th>
                <th>Large</th>
                <th>Subtle</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(({ name, figmaName, figmaNodeId, Component }) => (
                <tr key={name} data-icon={name}>
                  <td>{name}</td>
                  <td className="text-gray-500">
                    {figmaLinkOrTooltip(figmaName, figmaNodeId)}
                  </td>
                  <td>
                    <Component variant="sm" />
                  </td>
                  <td>
                    <Component variant="base" />
                  </td>
                  <td>
                    <Component variant="lg" />
                  </td>
                  <td>
                    <IconModule.IconSubtleProvider value={true}>
                      <Component variant="base" />
                    </IconModule.IconSubtleProvider>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Navigation = () => (
  <div className="pt-8 space-y-4">
    <h1 id="navigation" className={tokens.type.h1}>
      Navigation
    </h1>
    <div className="mt-4">
      <h3 className={tokens.type.h3}>Tabs</h3>
      <div className="mt-4">
        <Tabs
          tabs={[
            { name: "Tab 1", current: true, href: "#" },
            { name: "Tab 2", current: false, href: "/" },
            { name: "Tab 3", current: false, href: "/" },
            { name: "Tab 4", current: false, href: "/" },
            { name: "Tab 5", current: false, href: "/" },
            { name: "Tab 6", current: false, href: "/" },
          ]}
        />
      </div>
    </div>
    <div className="mt-4">
      <h3 className={tokens.type.h3}>Breadcrumbs</h3>
      <div className="mt-4">
        <Breadcrumbs
          crumbs={[
            { to: "/", name: "Crumb 1" },
            { to: "/", name: "Crumb 2" },
            { to: "/", name: "Crumb 3" },
            { to: "/", name: "Crumb 4" },
            { to: "/styles", name: "Crumb 5" },
          ]}
        />
      </div>
      <div className="mt-4">
        <Breadcrumbs
          crumbs={[
            {
              to: "/",
              name: "Sources",
              label: "Section",
              crumbVariant: CRUMB_VARIANTS.UPPERCASE,
            },
            {
              to: "/",
              name: "my-environment",
              label: "Environment",
              crumbVariant: CRUMB_VARIANTS.UPPERCASE,
            },
            {
              to: "/styles",
              name: "my-database",
              label: "Database",
              crumbVariant: CRUMB_VARIANTS.UPPERCASE,
            },
          ]}
        />
      </div>
    </div>
  </div>
);

const Info = () => (
  <div className="pt-8 space-y-4">
    <h1 id="tooltips" className={tokens.type.h1}>
      Tooltips
    </h1>
    <Tooltip text="Here is some help text!">
      <div>Here is a tooltip hover top</div>
    </Tooltip>
    <Tooltip text="Here is some help text! Here is some help text! Here is some help text! Here is some help text! Here is some help text!">
      <div>Here is a tooltip hover top long</div>
    </Tooltip>
    <Tooltip
      header={
        <>
          Scale: <span className="text-red-300">Under-Provisioned</span>
        </>
      }
      values={[
        { label: "CPU Usage (p95):", value: "35.0% / 12.5%" },
        { label: "RAM Usage (peak):", value: "94.5% / 100% (0.5GB / 0.5GB)" },
      ]}
      description="Last evaluated on Aug 19, 2026 at 04:47 UTC, based on prior 14 days. Note: For safety and reliability, we apply a 3x multiplier to memory recommendations for PostgreSQL databases."
      link={{ label: "Scale", to: "/styles" }}
    >
      <div>
        Here is a structured tooltip (header + values + description + link)
      </div>
    </Tooltip>
    <Tooltip
      header={
        <>
          Horizontal Autoscaling: <span className="text-lime">ON</span>
        </>
      }
      values={[
        { label: "Min. Containers:", value: "1" },
        { label: "Max Containers:", value: "4" },
        { label: "Scale Down CPU Threshold:", value: "0.1" },
        { label: "Scale Up CPU Threshold:", value: "0.9" },
      ]}
      link={{ label: "View settings", to: "/styles" }}
    >
      <div>Here is a structured tooltip (header + values + link)</div>
    </Tooltip>
  </div>
);

const ResourceDetailRedesign = () => {
  const db = defaultDeployDatabase({
    id: "222",
    handle: "my-postgres-db",
    type: "postgresql",
    createdAt: new Date("2024-11-02").toISOString(),
  });
  const service = defaultDeployService({
    id: "444",
    handle: "my-app-cmd",
    containerMemoryLimitMb: 4096,
    createdAt: new Date("2024-11-02").toISOString(),
  });
  const endpoint = defaultDeployEndpoint({
    id: "333",
    type: "http",
    status: "provisioned",
    virtualDomain: "app.example.com",
    createdAt: new Date("2024-11-02").toISOString(),
  });
  const stack = defaultDeployStack({
    id: "555",
    name: "my-stack",
    createdAt: new Date("2024-11-02").toISOString(),
  });
  const llmKey = defaultDeployLlmKey({
    id: "666",
    handle: "my-llm-key",
    createdAt: new Date("2024-11-02").toISOString(),
    currentUsage: {
      spend: 8.32,
      inputTokens: 0,
      outputTokens: 0,
      successfulRequests: 0,
      failedRequests: 0,
      totalRequests: 0,
      totalTokens: 0,
    },
  });
  const app = defaultDeployApp({
    id: "777",
    handle: "my-app",
    createdAt: new Date("2024-11-02").toISOString(),
  });
  const env = defaultDeployEnvironment({
    id: "888",
    handle: "my-environment",
    createdAt: new Date("2024-11-02").toISOString(),
  });

  return (
    <div className="flex flex-col gap-3 pt-8">
      <h1 id="resource-detail-boxes" className={tokens.type.h1}>
        Resource Detail Boxes
      </h1>

      <h2 className={tokens.type.h2}>4 Panel</h2>

      <h3 className={tokens.type.h3}>Database</h3>
      <ResourceDetailBar
        icon={
          <img
            src={`/database-types/logo-${db.type}.png`}
            className="w-[32px] h-[32px]"
            aria-label={`${db.type} Database`}
          />
        }
        name={db.handle}
        nameCopyText={db.handle}
        id={db.id}
        createdAt={db.createdAt}
        cost={49.99}
        costTooltip={COST_ESTIMATE_TOOLTIP_TEXT}
      />

      <h3 className={`${tokens.type.h3} pt-4`}>App</h3>
      <ResourceDetailBar
        icon={
          <img
            src="/resource-types/logo-app.png"
            className="w-[32px] h-[32px]"
            aria-label="App"
          />
        }
        nameLabel="App"
        name={app.handle}
        id={app.id}
        dateLabel="Created"
        createdAt={app.createdAt}
        cost={14.99}
        costTooltip={COST_ESTIMATE_TOOLTIP_TEXT}
      />

      <h3 className={`${tokens.type.h3} pt-4`}>Environment</h3>
      <ResourceDetailBar
        icon={
          <img
            src="/resource-types/logo-environment.png"
            className="w-[32px] h-[32px]"
            aria-label="Environment"
          />
        }
        nameLabel="Environment"
        name={env.handle}
        id={env.id}
        createdAt={env.createdAt}
        cost={64.99}
        costTooltip={COST_ESTIMATE_TOOLTIP_TEXT}
      />

      <h3 className={`${tokens.type.h3} pt-4`}>Service</h3>
      <ResourceDetailBar
        icon={
          <img
            src="/resource-types/logo-app.png"
            className="w-[32px] h-[32px]"
            aria-label="Service"
          />
        }
        nameLabel="Service"
        name={service.handle}
        id={service.id}
        dateLabel="Last Deploy"
        createdAt={service.createdAt}
        cost={24.99}
        costTooltip={COST_ESTIMATE_TOOLTIP_TEXT}
      />

      <h3 className={`${tokens.type.h3} pt-4`}>Stack</h3>
      <ResourceDetailBar
        icon={
          <img
            src={getStackImg(getStackType(stack))}
            className="w-[32px] h-[32px]"
            aria-label="Stack"
          />
        }
        nameLabel="Stack"
        name={stack.name}
        id={stack.id}
        createdAt={stack.createdAt}
        cost={0}
        costTooltip={COST_ESTIMATE_TOOLTIP_TEXT}
      />

      <h3 className={`${tokens.type.h3} pt-4`}>LLM Key</h3>
      <ResourceDetailBar
        icon={
          <img
            src="/resource-types/logo-llm_key.png"
            className="w-[32px] h-[32px]"
            aria-label="LLM Key"
          />
        }
        nameLabel="LLM Key"
        name={llmKey.handle}
        id={llmKey.id}
        createdAt={llmKey.createdAt}
        costLabel="Spend (MTD)"
        cost={llmKey.currentUsage.spend}
      />

      <h2 className={`${tokens.type.h2} pt-4`}>5 Panel</h2>

      <h3 className={tokens.type.h3}>Endpoint</h3>
      <ResourceDetailBar
        icon={
          <img
            src="/resource-types/logo-vhost.png"
            className="w-[32px] h-[32px]"
            aria-label="Endpoint"
          />
        }
        nameLabel="Hostname"
        name={endpoint.virtualDomain}
        nameCopyText={endpoint.virtualDomain}
        id={endpoint.id}
        url={
          <div className="flex flex-row items-center gap-2 min-w-0">
            <div className="min-w-0 break-words">
              <EndpointUrl enp={endpoint} />
            </div>
            <CopyTextButton
              text={endpoint.virtualDomain}
              className="shrink-0"
            />
          </div>
        }
        dateLabel="Provisioned"
        createdAt={endpoint.createdAt}
        cost={12.5}
        costTooltip={COST_ESTIMATE_TOOLTIP_TEXT}
      />

      <h2 className={`${tokens.type.h2} pt-4`}>Custom Resource Detail Box</h2>

      <h3 className={tokens.type.h3}>Database</h3>
      <ResourceDetailsTable columns={2}>
        <ResourceDetailItem title="Database Type">
          PostgreSQL 17
        </ResourceDetailItem>
        <ResourceDetailItem title="Container Profile">
          General Purpose (M)
        </ResourceDetailItem>
        <ResourceDetailItem title="Disk Size">100 GB</ResourceDetailItem>
        <ResourceDetailItem title="Memory Limit">4 GB</ResourceDetailItem>
        <ResourceDetailItem title="Disk Type">gp2</ResourceDetailItem>
        <ResourceDetailItem title="CPU Share">0.125</ResourceDetailItem>
        <ResourceDetailItem title="Disk IOPS">100 IOPS</ResourceDetailItem>
        <ResourceDetailItem title="Point-in-Time-Recovery">
          Enabled
        </ResourceDetailItem>
        <ResourceDetailItem title="Disk Encryption">AES-256</ResourceDetailItem>
        <ResourceDetailItem title="Container Efficiency">
          <ScaleRecsView service={service} />
        </ResourceDetailItem>
      </ResourceDetailsTable>

      <h3 className={tokens.type.h3}>Service</h3>
      <ResourceDetailsTable columns={2}>
        <ResourceDetailItem title="Type">
          <Code>cmd</Code>
        </ResourceDetailItem>
        <ResourceDetailItem title="Container Count">1</ResourceDetailItem>
        <ResourceDetailItem title="Resource">
          <DataTableCell
            label="my-app"
            href="#"
            icon={
              <img
                src="/resource-types/logo-app.png"
                className="w-[20px] h-[20px] mr-2"
                aria-label="App"
              />
            }
          />
        </ResourceDetailItem>
        <ResourceDetailItem title="Container Size">0.5 GB</ResourceDetailItem>
        <ResourceDetailItem title="Deployment Strategy">
          <DataTableCell label="Zero-Overlap" href="#" />
        </ResourceDetailItem>
        <ResourceDetailItem title="CPU Share">0.125</ResourceDetailItem>
        <ResourceDetailItem title="Container Profile">
          General Purpose (M)
        </ResourceDetailItem>
        <ResourceDetailItem title="Container Efficiency">
          <ScaleRecsView service={service} />
        </ResourceDetailItem>
      </ResourceDetailsTable>

      <h3 className={tokens.type.h3}>Stack</h3>
      <ResourceDetailsTable columns={2}>
        <ResourceDetailItem title="Tenancy">Dedicated</ResourceDetailItem>
        <ResourceDetailItem
          title="Network CIDR"
          titleTooltip="The IP address range assigned to this Stack's VPC."
        >
          10.10.0.0/16
        </ResourceDetailItem>
        <ResourceDetailItem title="Region">us-east-1</ResourceDetailItem>
        <ResourceDetailItem
          title="Outbound IP Addresses"
          titleTooltip="The IP addresses your resources use to make outbound connections."
        >
          18.205.248.132, 3.210.50.104, 52.55.114.39, 34.203.74.106,
          34.236.214.101
        </ResourceDetailItem>
      </ResourceDetailsTable>

      <h3 className={tokens.type.h3}>Endpoint</h3>
      <ResourceDetailsTable columns={2}>
        <ResourceDetailItem title="Type">
          <Code>tls</Code>
        </ResourceDetailItem>
        <ResourceDetailItem title="Placement">Public</ResourceDetailItem>
        <ResourceDetailItem title="Service Process">
          <Code>cmd</Code>
        </ResourceDetailItem>
        <ResourceDetailItem title="Port">3000</ResourceDetailItem>
        <ResourceDetailItem title="Service ID">44505</ResourceDetailItem>
        <ResourceDetailItem title="Using Header Auth">False</ResourceDetailItem>
        <ResourceDetailItem title="Resource">
          <DataTableCell
            label="dashboard-database"
            href="#"
            icon={
              <img
                src={`/database-types/logo-${db.type}.png`}
                className="w-[20px] h-[20px] mr-2"
                aria-label={`${db.type} Database`}
              />
            }
          />
        </ResourceDetailItem>
        <ResourceDetailItem title="Status">Provisioned</ResourceDetailItem>
        <ResourceDetailItem title="Shared">N/A</ResourceDetailItem>
        <ResourceDetailItem title="IP Allowlist">Disabled</ResourceDetailItem>
      </ResourceDetailsTable>
    </div>
  );
};

const NegativeSpace = () => {
  return (
    <Group>
      <div className="pt-8">
        <h1 id="boxes" className={tokens.type.h1}>
          Box
        </h1>
        <Box>A simple box</Box>
      </div>

      <div className="pt-8">
        <h1 className={tokens.type.h1}>Box Group</h1>
        <div>
          <Group>
            <Box>One</Box>
            <Box>Two</Box>
            <Box>Three</Box>
          </Group>
        </div>

        <div className="pt-8">
          <h2 className={tokens.type.h2}>Box Group Horizontal</h2>
          <Group variant="horizontal">
            <Box>One</Box>
            <Box>Two</Box>
            <Box>Three</Box>
          </Group>
        </div>
      </div>
    </Group>
  );
};

const QRExample = () => {
  return (
    <Group>
      <div className="pt-8">
        <h1 id="qrexample" className={tokens.type.h1}>
          QR Example
        </h1>
      </div>
      <QRCodeSVG value="1234567891011121314151617" />
    </Group>
  );
};

export const StylesPage = () => {
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      el.scrollIntoView({ behavior: "instant" });
    }
  }, []);

  return (
    <div className="px-4 py-4">
      <StylesWrapper navigation={<StylesNavigation />}>
        <Colors />
        <Typography />
        <AiTerms />
        <Buttons />
        <Banners />
        <Tables />
        <DataTables />
        <Data />
        <Forms />
        <ProgressBars />
        <SegmentedBars />
        <Logs />
        <Pills />
        <Labels />
        <Navigation />
        <Icons />
        <Info />
        <ResourceDetailRedesign />
        <NegativeSpace />
        <QRExample />
      </StylesWrapper>
    </div>
  );
};
